/**
 * Coding rules: which Xero account a purchase invoice line goes to, by what was bought (a
 * dictionary item, a supplier route) or who it was from (a supplier). A line
 * takes the first rule that applies, in that order.
 *
 * - `CodingRulesTab` is every current rule, of every scope: the Ledger rules
 *   page mounts it as its Coding tab (the accruals stream owns that page; this
 *   stream owns the coding domain). Filtered, searched and paged by the view.
 * - `CodingRulesTable` and `NewCodingRuleDialog` are its parts; the supplier
 *   page shows one supplier's rules with them, and starts a new rule on that
 *   supplier.
 *
 * Every rule is written to the ledger at the collection URL a read carried; a
 * new version supersedes the subject's current rule, and purchase invoices already
 * confirmed keep their coding.
 */
import { useEffect, useId, useState } from "react";
import { toast } from "sonner";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type {
  CodingRule,
  CodingRuleListPage,
  CodingRuleScope,
  CodingRuleSubject,
  VatTreatment,
  WriteCodingRuleInput,
} from "@shared-core/data/purchase-ledger-types";
import { extractFieldErrors } from "@shared-core/data/validation";
import {
  VAT_TREATMENT_OPTIONS,
  formatDate,
} from "@shared-core/domain/purchase-ledger";
import { useDebouncedValue } from "@shared-core/domain/useDebouncedValue";
import { ListPagination } from "../../patterns/ListPagination";
import { Button } from "../../primitives/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../primitives/dialog";
import { Input } from "../../primitives/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../primitives/select";
import { Skeleton } from "../../primitives/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../primitives/table";

export const CODING_RULE_SCOPES: { value: CodingRuleScope; label: string }[] = [
  { value: "dictionary_item", label: "Dictionary item" },
  { value: "service_kind", label: "Service" },
  { value: "route", label: "Supplier route" },
  { value: "supplier", label: "Supplier" },
];

/** A rule's VAT is only for a line whose document shows none: the zero-rate treatments. */
const RULE_VAT_OPTIONS = VAT_TREATMENT_OPTIONS.filter(
  (o) => o.value !== "standard" && o.value !== "reduced",
);
const NO_TREATMENT = "none";
const ALL_SCOPES = "all";
const PAGE_SIZE = 50;

export function CodingRulesTable({
  rules,
  empty,
}: {
  rules: CodingRule[];
  /** What to say when there are none. */
  empty: string;
}) {
  if (rules.length === 0) {
    return <p className="text-muted-foreground text-sm">{empty}</p>;
  }
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>When</TableHead>
          <TableHead>Xero account</TableHead>
          <TableHead>Set</TableHead>
          <TableHead className="text-right">Lines, last 12 months</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rules.map((rule) => (
          <TableRow key={rule.id}>
            <TableCell className="whitespace-normal">
              <div className="text-muted-foreground text-xs">
                {rule.scopeLabel}
              </div>
              <div>{rule.subject}</div>
            </TableCell>
            <TableCell>
              {rule.xeroAccount}
              {rule.vatTreatmentLabel && (
                <div className="text-muted-foreground text-xs">
                  No VAT shown: {rule.vatTreatmentLabel}
                </div>
              )}
            </TableCell>
            <TableCell className="text-muted-foreground text-sm">
              {formatDate(rule.setOn)} by {rule.setBy}
            </TableCell>
            <TableCell className="text-right tabular-nums">
              {rule.linesLast12Months}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

/** What a new rule names: chosen from a search, or fixed by where it was started (a supplier's page). */
function SubjectPicker({
  id,
  scope,
  value,
  onChange,
  error,
}: {
  id: string;
  scope: CodingRuleScope;
  value: CodingRuleSubject | null;
  onChange: (subject: CodingRuleSubject | null) => void;
  error?: string;
}) {
  const adapter = useDataAdapter();
  const [search, setSearch] = useState("");
  const debounced = useDebouncedValue(search.trim(), 250);
  const [found, setFound] = useState<CodingRuleSubject[]>([]);
  const [failed, setFailed] = useState<string | null>(null);
  useEffect(() => {
    if (value) return;
    let cancelled = false;
    adapter
      .searchCodingRuleSubjects({ scope, q: debounced || undefined })
      .then((subjects) => {
        if (cancelled) return;
        setFound(subjects);
        setFailed(null);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setFound([]);
          setFailed(err instanceof Error ? err.message : "The search failed.");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [adapter, scope, debounced, value]);
  return (
    <div className="space-y-1.5">
      <Input
        id={id}
        value={value ? value.label : search}
        onChange={(e) => {
          onChange(null);
          setSearch(e.target.value);
        }}
        placeholder="Search…"
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${id}-error` : undefined}
      />
      {!value && found.length > 0 && (
        <ul
          aria-label="Matches"
          className="max-h-40 overflow-y-auto rounded-md border"
        >
          {found.map((subject) => (
            <li key={subject.key}>
              <button
                type="button"
                className="hover:bg-muted w-full px-3 py-1.5 text-left text-sm"
                onClick={() => onChange(subject)}
              >
                {subject.label}
              </button>
            </li>
          ))}
        </ul>
      )}
      {failed && (
        <p role="alert" className="text-sm text-red-600">
          {failed}
        </p>
      )}
      {error && (
        <p id={`${id}-error`} className="text-sm text-red-600">
          {error}
        </p>
      )}
    </div>
  );
}

/**
 * A new coding rule of any scope: what it names, the Xero account, and the
 * VAT treatment for a line whose document shows none. Started from a
 * supplier's page it names that supplier unless another scope is chosen.
 */
export function NewCodingRuleDialog({
  startOn,
  onCreate,
}: {
  /** The supplier the dialog starts on (a supplier page); omitted, the person picks. */
  startOn?: CodingRuleSubject;
  onCreate: (input: WriteCodingRuleInput) => Promise<void>;
}) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const [scope, setScope] = useState<CodingRuleScope>(
    startOn ? "supplier" : "dictionary_item",
  );
  const [subject, setSubject] = useState<CodingRuleSubject | null>(
    startOn ?? null,
  );
  const [account, setAccount] = useState("");
  const [treatment, setTreatment] = useState<string>(NO_TREATMENT);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  function reset() {
    setScope(startOn ? "supplier" : "dictionary_item");
    setSubject(startOn ?? null);
    setAccount("");
    setTreatment(NO_TREATMENT);
    setFields({});
    setMessage(null);
  }

  async function submit() {
    if (!subject) return;
    setSaving(true);
    setFields({});
    setMessage(null);
    try {
      await onCreate({
        scope,
        subjectKey: subject.key,
        xeroAccountCode: account.trim(),
        vatTreatment:
          treatment === NO_TREATMENT ? null : (treatment as VatTreatment),
      });
      setOpen(false);
      reset();
    } catch (err) {
      const fieldErrors = extractFieldErrors(err);
      if (fieldErrors.length > 0) {
        setFields(
          Object.fromEntries(fieldErrors.map((e) => [e.field, e.message])),
        );
      } else {
        setMessage(
          err instanceof Error ? err.message : "The rule was not saved.",
        );
      }
    } finally {
      setSaving(false);
    }
  }

  const accountError = fields.xero_account_code;
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) reset();
      }}
    >
      <DialogTrigger asChild>
        <Button size="sm">New coding rule</Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>New coding rule</DialogTitle>
          <DialogDescription>
            Purchase invoice lines it reaches code to this account. It replaces
            the subject&apos;s current rule; purchase invoices already confirmed
            keep their coding.
          </DialogDescription>
        </DialogHeader>
        {(message || Object.keys(fields).length > 0) && (
          <p role="alert" className="text-sm text-red-600">
            {message ?? "Correct what is marked below."}
          </p>
        )}
        <div className="space-y-1.5">
          <label htmlFor={`${id}-scope`} className="text-sm font-medium">
            When a line is for
          </label>
          <Select
            value={scope}
            onValueChange={(next) => {
              setScope(next as CodingRuleScope);
              setSubject(next === "supplier" && startOn ? startOn : null);
            }}
          >
            <SelectTrigger id={`${id}-scope`} aria-label="When a line is for">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CODING_RULE_SCOPES.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <label htmlFor={`${id}-subject`} className="text-sm font-medium">
            {CODING_RULE_SCOPES.find((s) => s.value === scope)?.label}
          </label>
          <SubjectPicker
            key={scope}
            id={`${id}-subject`}
            scope={scope}
            value={subject}
            onChange={setSubject}
            error={fields.subject_key}
          />
        </div>
        <div className="space-y-1.5">
          <label htmlFor={`${id}-account`} className="text-sm font-medium">
            Xero account code
          </label>
          <Input
            id={`${id}-account`}
            value={account}
            onChange={(e) => setAccount(e.target.value)}
            placeholder="e.g. 429"
            aria-invalid={accountError ? true : undefined}
            aria-describedby={accountError ? `${id}-account-error` : undefined}
          />
          {accountError && (
            <p id={`${id}-account-error`} className="text-sm text-red-600">
              {accountError}
            </p>
          )}
        </div>
        <div className="space-y-1.5">
          <label className="text-sm font-medium" htmlFor={`${id}-vat`}>
            VAT when the document shows none
          </label>
          <Select value={treatment} onValueChange={setTreatment}>
            <SelectTrigger
              id={`${id}-vat`}
              aria-label="VAT when the document shows none"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NO_TREATMENT}>
                Leave it to the reviewer
              </SelectItem>
              {RULE_VAT_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <DialogFooter>
          <Button
            onClick={submit}
            disabled={!subject || account.trim() === "" || saving}
          >
            Save rule
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

type ListState =
  | { kind: "loading" }
  | { kind: "failed"; message: string }
  | { kind: "loaded"; page: CodingRuleListPage };

/** Every current coding rule, of every scope: the Ledger rules page's Coding tab. */
export function CodingRulesTab() {
  const adapter = useDataAdapter();
  const [scope, setScope] = useState<string>(ALL_SCOPES);
  const [query, setQuery] = useState("");
  const debounced = useDebouncedValue(query.trim(), 300);
  const [pageNo, setPageNo] = useState(1);
  const [reloads, setReloads] = useState(0);
  const [state, setState] = useState<ListState>({ kind: "loading" });

  useEffect(() => {
    let cancelled = false;
    adapter
      .listCodingRules({
        scope: scope === ALL_SCOPES ? undefined : [scope as CodingRuleScope],
        q: debounced || undefined,
        page: pageNo,
        pageSize: PAGE_SIZE,
      })
      .then((page) => {
        if (!cancelled) setState({ kind: "loaded", page });
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setState({
            kind: "failed",
            message:
              err instanceof Error ? err.message : "The rules did not load.",
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [adapter, scope, debounced, pageNo, reloads]);

  const page = state.kind === "loaded" ? state.page : null;
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <p className="text-muted-foreground max-w-2xl text-sm">
          A purchase invoice line takes the first rule that applies: dictionary
          item, then supplier route, then supplier. A reviewer can still change
          one line, with a reason. Changing a rule never recodes a purchase
          invoice already confirmed.
        </p>
        {page?.canManageRules && (
          <NewCodingRuleDialog
            onCreate={async (input) => {
              await adapter.writeCodingRule(page.codingRulesUrl, input);
              toast.success("Coding rule saved");
              setReloads((n) => n + 1);
            }}
          />
        )}
      </div>
      <div className="flex flex-wrap gap-2">
        <Select
          value={scope}
          onValueChange={(next) => {
            setScope(next);
            setPageNo(1);
          }}
        >
          <SelectTrigger className="h-9 w-[12rem]" aria-label="Which rules">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_SCOPES}>Every rule</SelectItem>
            {CODING_RULE_SCOPES.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setPageNo(1);
          }}
          placeholder="What it names, or the account…"
          aria-label="Search coding rules"
          className="h-9 w-full sm:w-72"
        />
      </div>
      {state.kind === "loading" && (
        <Skeleton className="h-40 w-full" aria-label="Loading coding rules" />
      )}
      {state.kind === "failed" && (
        <p role="alert" className="text-sm text-red-600">
          Could not load the coding rules: {state.message}
        </p>
      )}
      {page && (
        <>
          <CodingRulesTable
            rules={page.items}
            empty={
              debounced || scope !== ALL_SCOPES
                ? "No rule matches that."
                : "No coding rules yet."
            }
          />
          {page.totalPages > 1 && (
            <ListPagination
              page={page.page}
              totalPages={page.totalPages}
              onPageChange={setPageNo}
            />
          )}
        </>
      )}
    </div>
  );
}
